import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { retroTimestamp, fullTimestamp } from '@/lib/retroTime';
import { NoteContent } from './NoteContent';

interface RetroNoteCardProps {
  event: NostrEvent;
  index?: number;
}

export function RetroNoteCard({ event, index }: RetroNoteCardProps) {
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;

  const displayName = metadata?.display_name ?? metadata?.name ?? genUserName(event.pubkey);
  const picture = metadata?.picture;

  const npub = useMemo(() => {
    try {
      return nip19.npubEncode(event.pubkey);
    } catch {
      return '';
    }
  }, [event.pubkey]);

  const nevent = useMemo(() => {
    try {
      return nip19.neventEncode({ id: event.id, author: event.pubkey });
    } catch {
      return '';
    }
  }, [event.id, event.pubkey]);

  const recordNumber = typeof index === 'number'
    ? String(index + 1).padStart(3, '0')
    : event.id.slice(0, 4).toUpperCase();

  return (
    <article className="relative pixel-border bg-card/80 backdrop-blur-sm group">
      {/* Top status bar — CRT terminal style */}
      <div className="flex items-center justify-between border-b-2 border-primary/70 bg-black/60 px-3 py-1.5">
        <div className="flex items-center gap-2 font-pixel text-[10px] tracking-widest">
          <span className="text-neon-green text-glow-green">●</span>
          <span className="text-neon-cyan text-glow-cyan">REC.{recordNumber}</span>
          <span className="text-muted-foreground">//</span>
          <span className="text-neon-amber">KIND:01</span>
        </div>
        <time
          dateTime={new Date(event.created_at * 1000).toISOString()}
          title={fullTimestamp(event.created_at)}
          className="font-pixel text-[10px] text-neon-amber text-glow-amber tracking-widest"
        >
          T-{retroTimestamp(event.created_at)}
        </time>
      </div>

      <div className="p-4 md:p-5 space-y-3">
        {/* Author row */}
        <div className="flex items-center gap-3">
          {npub ? (
            <Link
              to={`/${npub}`}
              className="shrink-0 pixel-border-cyan bg-black p-0.5 block hover:-translate-y-0.5 transition-transform"
            >
              <AuthorAvatar picture={picture} name={displayName} />
            </Link>
          ) : (
            <div className="shrink-0 pixel-border-cyan bg-black p-0.5">
              <AuthorAvatar picture={picture} name={displayName} />
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="font-pixel text-sm md:text-base text-neon-magenta text-glow-magenta truncate">
              {displayName}
            </div>
            <div className="flex items-center gap-2 font-mono text-[10px] text-muted-foreground truncate">
              <span className="text-neon-green">&gt;</span>
              <span className="truncate">{npub ? shortNpub(npub) : event.pubkey.slice(0, 12)}</span>
              {metadata?.nip05 && (
                <>
                  <span className="text-muted-foreground/50">|</span>
                  <span className="text-neon-cyan truncate">{metadata.nip05}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="font-mono text-sm md:text-[15px]">
          <NoteContent event={event} />
        </div>

        {/* Footer bar */}
        <div className="pt-2 border-t border-primary/30 flex items-center justify-between font-pixel text-[9px] tracking-widest">
          <span className="text-muted-foreground">
            BYTES:<span className="text-neon-cyan">{event.content.length.toString().padStart(4, '0')}</span>
          </span>
          <span className="text-muted-foreground">
            TAGS:<span className="text-neon-green">{String(event.tags.length).padStart(2, '0')}</span>
          </span>
          {nevent && (
            <Link
              to={`/${nevent}`}
              className="text-neon-amber hover:text-glow-amber"
            >
              [VIEW]
            </Link>
          )}
        </div>
      </div>

      {/* corner brackets — decorative CRT overlay */}
      <span aria-hidden className="pointer-events-none absolute -top-1 -left-1 w-3 h-3 border-t-2 border-l-2 border-neon-cyan" />
      <span aria-hidden className="pointer-events-none absolute -top-1 -right-1 w-3 h-3 border-t-2 border-r-2 border-neon-cyan" />
      <span aria-hidden className="pointer-events-none absolute -bottom-1 -left-1 w-3 h-3 border-b-2 border-l-2 border-neon-cyan" />
      <span aria-hidden className="pointer-events-none absolute -bottom-1 -right-1 w-3 h-3 border-b-2 border-r-2 border-neon-cyan" />
    </article>
  );
}

function AuthorAvatar({ picture, name }: { picture?: string; name: string }) {
  if (picture) {
    return (
      <img
        src={picture}
        alt=""
        loading="lazy"
        className="block w-11 h-11 md:w-12 md:h-12 object-cover bg-black"
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />
    );
  }
  return (
    <div className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center bg-gradient-to-br from-primary to-accent font-pixel text-lg text-primary-foreground">
      {name[0]?.toUpperCase() ?? '?'}
    </div>
  );
}

function shortNpub(npub: string): string {
  if (npub.length <= 22) return npub;
  return `${npub.slice(0, 11)}…${npub.slice(-6)}`;
}
